import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonDialogsService } from '../common-dialogs.service';
import { ComponentAccessor } from 'idk/widgets/dynamic-component-injector/component-accessor';
import { ComponentDescriptor } from 'idk/widgets/dynamic-component-injector/component-descriptor';
import { DynamicContainerComponent } from 'idk/widgets/dynamic-component-injector/dynamic-container/dynamic-container.component';

@Component({
    selector: 'app-default-dialog-layout',
    templateUrl: './default-dialog-layout.component.html',
    host: { class: 'flex flex-col h-full' },
    standalone: false,
})
export class DefaultDialogLayoutComponent implements OnInit {
    @ViewChild('dcContainer')
    public dcContainer: DynamicContainerComponent;

    public canClose: boolean;

    public leftButtons: DefaultDialogButtonSettings[];

    public rightButtons: DefaultDialogButtonSettings[];

    public button1: DefaultDialogButtonSettings;

    public button2: DefaultDialogButtonSettings;

    public constructor(
        private _dialogs: CommonDialogsService,
        private _dialog: MatDialogRef<DefaultDialogLayoutComponent>,

        @Inject(MAT_DIALOG_DATA)
        public config: DefaultDialogLayoutSettings<any, any>
    ) {}

    public ngOnInit(): void {
        this.canClose = this.config.canClose ?? true;

        this.leftButtons = this.config.leftButtons ?? [];

        this.rightButtons = this.config.rightButtons ?? [];

        this.button1 = Object.assign(
            {
                title: 'Aceptar',
                icon: 'heroicons_outline:check-circle',
                visible: true,
                disabled: false,
            },
            this.config.button1
        );

        this.button2 = Object.assign(
            {
                title: 'Cancelar',
                icon: 'heroicons_outline:cancel_circle',
                visible: true,
                disabled: false,
            },
            this.config.button2
        );
    }

    public onClose(): void {
        const component: ComponentAccessor<any> = this.dcContainer.component;
        if (component.onClose) {
            const result: boolean | Promise<boolean> = component.onClose();

            if (!result) {
                this._dialog.close();
                return;
            }

            if (typeof result === 'boolean') {
                if (result) {
                    this._dialog.close(component.context);
                }
            } else {
                try {
                    result.then((r) => {
                        if (r) {
                            this._dialog.close(component.context);
                        }
                    });
                } catch {
                    throw new Error(
                        `El valor "${result}" no reconoce como una instancia del tipo boolean ó Promise<boolean>`
                    );
                }
            }
        } else {
            this._dialog.close();
        }
    }

    public onButton1Click(): void {
        if (this.button1.action) {
            this.button1.action(() => this._dialog.close());
        } else {
            const component: ComponentAccessor<any> = this.dcContainer.component;
            if (!component.onSubmit) {
                this._dialogs.error(
                    'El componente debe de implementar el método onSubmit():boolean | Promise<boolean> | void para responde a este evento'
                );
                return;
            }

            let resultValue: any | Promise<any> = null;

            let returnValue: boolean | Promise<boolean> | void =
                component.onSubmit((value) => (resultValue = value));

            if (typeof returnValue == 'undefined') {
                Promise.resolve(resultValue).then((result) =>
                    this._dialog.close(result)
                );
            } else {
                Promise.resolve<boolean>(returnValue).then((value) => {
                    if (value) {
                        Promise.resolve(resultValue).then((result) =>
                            this._dialog.close(result)
                        );
                    }
                });
            }
        }
    }

    public onButton2Click(): void {
        const component: ComponentAccessor<any> = this.dcContainer.component;
        if (component.onCancel) {
            const result: boolean | Promise<boolean> = component.onCancel();

            if (!result) {
                this._dialog.close();
                return;
            }

            if (typeof result === 'boolean') {
                if (result) {
                    this._dialog.close(component.context);
                }
            } else {
                try {
                    result.then((r) => {
                        if (r) {
                            this._dialog.close(component.context);
                        }
                    });
                } catch {
                    throw new Error(
                        `El valor "${result}" no reconoce como una instancia del tipo boolean ó Promise<boolean>`
                    );
                }
            }
        } else {
            if (this.button2.action) {
                this.button2.action(() => this._dialog.close());
            } else {
                this._dialog.close();
            }
        }
    }

    public onLeftButtonClick(index: number): void {
        this.leftButtons[index].action(() => this._dialog.close());
    }

    public onRightButtonClick(index: number): void {
        this.rightButtons[index].action(() => this._dialog.close());
    }
}

export interface DefaultDialogLayoutSettings<TComponent, TContext> {
    title: string;

    descriptor: ComponentDescriptor<TComponent, TContext>;

    canClose?: boolean;

    button1?: DefaultDialogButtonSettings;

    button2?: DefaultDialogButtonSettings;

    leftButtons?: DefaultDialogButtonSettings[];

    rightButtons?: DefaultDialogButtonSettings[];
}

export interface DefaultDialogButtonSettings {
    title?: string;

    tooltip?: string;

    icon?: string;

    action?: (closeCallback: () => void) => void;

    disabled?: boolean;

    visible?: boolean;
}
